import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/lib/testIds";
import { Mail, Phone, ArrowUpRight } from "lucide-react";
import FitText from "@/components/FitText";

export default function FinalCta() {
  const { t } = useI18n();

  return (
    <section
      data-testid={TID.finalCta}
      className="relative bg-[color:var(--gc-navy)] text-white py-28 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 gc-grid-bg-dark opacity-60" />
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[color:var(--gc-red)]/12 blur-3xl" />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl"
        >
          <p className="gc-eyebrow text-white/60">{t("final_cta.eyebrow")}</p>
          <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight font-[700]">
            {t("final_cta.title")}
          </h2>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
            {t("final_cta.lead")}
          </p>
          <div className="mt-10">
            <Link
              data-testid="final-cta-button"
              to="/contact"
              className="gc-btn bg-white text-[color:var(--gc-ink)] hover:bg-white/90"
            >
              <span>{t("common.cta_primary")}</span>
              <ArrowUpRight className="w-4 h-4 gc-arrow" />
            </Link>
          </div>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <motion.a
            data-testid={TID.ctaEmail}
            href={`mailto:${t("common.email")}`}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="group relative bg-[color:var(--gc-navy)] hover:bg-[color:var(--gc-ink)] transition-colors p-8 md:p-12 flex flex-col gap-6"
          >
            <div className="flex items-center justify-between">
              <span className="gc-eyebrow text-white/50">{t("final_cta.label_email")}</span>
              <Mail className="w-5 h-5 text-white/60 group-hover:text-white" />
            </div>
            <div className="font-display font-[700] tracking-tight">
              <FitText min={20} max={64}>{t("common.email")}</FitText>
            </div>
            <div className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.14em] text-white/70 group-hover:text-white">
              <span className="w-6 h-px bg-[color:var(--gc-red)]" />
              {t("final_cta.label_email")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
          </motion.a>

          <motion.a
            data-testid={TID.ctaPhone}
            href={t("common.phone_href")}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="group relative bg-[color:var(--gc-navy)] hover:bg-[color:var(--gc-ink)] transition-colors p-8 md:p-12 flex flex-col gap-6"
          >
            <div className="flex items-center justify-between">
              <span className="gc-eyebrow text-white/50">{t("final_cta.label_phone")}</span>
              <Phone className="w-5 h-5 text-white/60 group-hover:text-white" />
            </div>
            <div className="font-display font-[700] tracking-tight">
              <FitText min={20} max={64}>{t("common.phone")}</FitText>
            </div>
            <div className="text-sm text-white/60 font-mono tracking-wider">{t("final_cta.hours")}</div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
